import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Phone, Mail, MapPin, Send, CheckCircle, Building2, Ruler, Building, PencilRuler, ClipboardList, Target, Shield, Clock, Users, Star, Quote } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import bgimage from '../Assets/back.png';
import bgimage2 from '../Assets/back2.png';
import bgimage3 from '../Assets/back3.png';
import about from '../Assets/about.png';
import project1 from "../Assets/project1.png";
import magolis from "../Assets/margolis.png";
import vainona from "../Assets/Vainona.png";
import architectureImg from "../Assets/design.jpg";
import engineeringImg from "../Assets/eng.jpg";
import townPlanningImg from "../Assets/team4.jpg";
import interiorImg from "../Assets/work.jpg";
import './styles/Home.css';
import { Helmet } from 'react-helmet';

const Counter = ({ end, duration = 2, suffix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let startTime;
    let animationFrame;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const testimonials = [
  {
    name: "Mr. Dhliwayo",
    role: "Homeowner, Borrowdale",
    text: "BuildFast turned our dream into reality. The attention to detail and professionalism throughout the project was outstanding. Our mansion exceeded all expectations.",
    rating: 5,
  },
  {
    name: "Stephen Margolis",
    role: "Business Owner",
    text: "Working with BuildFast on our shopping mall was a seamless experience. They delivered on time and the quality of work speaks for itself. Highly recommended.",
    rating: 5,
  },
  {
    name: "Vainona Development Trust",
    role: "Community Project",
    text: "The clusters project transformed our community. BuildFast's team showed incredible vision and executed flawlessly from planning to completion.",
    rating: 5,
  },
];

export const Home = ({title}) => {
  const [currentBg, setCurrentBg] = useState(bgimage);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState('');
  const statsRef = useRef(null);

  const handleProjectClick = () => navigate('/projects');

  useEffect(() => { document.title = title; }, [title]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    try {
      const response = await fetch("https://formspree.io/f/myzypawe", {
        method: "POST", body: data, headers: { 'Accept': 'application/json' }
      });
      if (response.ok) { setFormStatus("Thanks for your submission!"); form.reset(); }
      else { setFormStatus("Oops! There was a problem submitting your form"); }
    } catch (error) { setFormStatus("Oops! There was a problem submitting your form"); }
  };

  const bgImages = useMemo(() => [bgimage, bgimage2, bgimage3], []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % bgImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [bgImages.length]);

  useEffect(() => { setCurrentBg(bgImages[currentBgIndex]); }, [currentBgIndex, bgImages]);

  const handleServices = () => navigate("/services/services");
  const handleClick = () => navigate('/about_build_fast');
  const handleServiceClick = (service) => navigate(`/services/${service.toLowerCase().replace(' ', '-')}`);
  const handleQuotation = () => navigate('/quotation');

  const projects = [
    { title: "MANSION FOR MR DHIHWA IN BORROWDALE", description: "An opulent mansion featuring cutting-edge architectural design and luxurious interiors, crafted to reflect elegance and comfort in the prestigious Borrowdale neighborhood.", image: project1 },
    { title: "STEPHEN MARGOLIS' SHOPPING MALL", description: "A dynamic and expansive shopping mall offering a blend of modern retail spaces, entertainment hubs, and sustainable design to create a top-tier shopping destination.", image: magolis },
    { title: "VAINONA CLUSTERS", description: "A thoughtfully designed residential development offering a blend of contemporary homes and community-driven living, transforming Vainona into a vibrant, harmonious neighborhood.", image: vainona },
  ];

  const services = [
    { title: "Architecture", description: "We craft innovative and functional designs that harmonize with their surroundings and reflect our clients' visions.", image: architectureImg, icon: Building2 },
    { title: "Engineering", description: "Our engineering team integrates structural, mechanical, and electrical systems to ensure seamless functionality and safety.", image: engineeringImg, icon: Ruler },
    { title: "Town Planning", description: "We offer expert town planning services to help shape the development of communities and urban spaces.", image: townPlanningImg, icon: Building },
    { title: "Interior Design", description: "Our interior design team creates spaces that are not only beautiful but also functional, enhancing the quality of life.", image: interiorImg, icon: PencilRuler },
    { title: "Project Management", description: "We manage all aspects of construction projects to ensure they are completed on time, within budget, and to the highest standards.", image: "https://images.pexels.com/photos/7376/startup-photos.jpg?auto=compress&cs=tinysrgb&w=600", icon: ClipboardList },
  ];

  const pricingTiers = [
    { size: "200-300SQM", price: "175", features: ["Main House Plan", "Durawall (Boundary) Wall Plan", "Bonus: 3D Images"] },
    { size: "350-500SQM", price: "315", popular: true, features: ["Main House Plan", "Cottage Plan", "Durawall (Boundary) Wall Plan", "Approval Assistance", "Bonus: 3D Images"] },
    { size: "500-1000SQM", price: "420", features: ["Main House Plan", "Cottage Plan", "Durawall (Boundary) Wall Plan", "Approval Assistance", "Bonus: 3D Images"] },
    { size: "1000+SQM Single", price: "610", features: ["Single Storey Plan", "Cottage Plan", "Durawall (Boundary) Wall Plan", "Approval Assistance", "Bonus: 3D Images"] },
    { size: "1000+SQM Double", price: "965", features: ["Double Storey Plan", "Cottage Plan", "Boundary Wall Plan", "Engineering Drawings", "Approval Assistance", "Bonus: 3D Images"] },
  ];

  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main className="overflow-x-hidden">
      <Helmet>
        <meta name="google-site-verification" content="q2KMOxqP0WRZnd8BDIXv-m18BP7E8GapB0_6ZOX9pZM" />
      </Helmet>

      {/* ============ HERO SECTION ============ */}
      <section className="relative h-screen overflow-hidden">
        <AnimatePresence initial={false}>
          <motion.div
            key={currentBgIndex}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${currentBg})` }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        <div className="absolute inset-0 bg-blue-900/20" />

        <div className="relative h-full flex flex-col items-center justify-center z-10 px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-block px-4 py-2 bg-yellow-400/20 backdrop-blur-sm border border-yellow-400/30 rounded-full text-yellow-400 text-sm font-medium mb-6 tracking-wider uppercase"
            >
              Zimbabwe's Premier Construction Company
            </motion.span>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white mb-6 leading-tight"
              style={{ textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}>
              BuildFast<br />
              <span className="text-yellow-400">Structural Solutions</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto font-light leading-relaxed">
              YOU DREAM IT, WE DESIGN IT, WE BUILD IT!
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.button
                onClick={handleServices}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-yellow-400 text-blue-900 font-bold py-4 px-10 rounded-xl text-lg hover:bg-yellow-300 transition-all duration-300 shadow-2xl shadow-yellow-400/25 flex items-center gap-2"
              >
                Explore Our Services
                <ChevronRight size={20} />
              </motion.button>
              <motion.button
                onClick={handleQuotation}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-bold py-4 px-10 rounded-xl text-lg hover:bg-white/20 transition-all duration-300"
              >
                Get a Free Quote
              </motion.button>
            </div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap items-center justify-center gap-6 md:gap-12 mt-12 pt-8 border-t border-white/10"
            >
              {[
                { value: '10+', label: 'Years Experience' },
                { value: '200+', label: 'Projects Completed' },
                { value: '100%', label: 'Client Satisfaction' },
                { value: '50+', label: 'Expert Team' },
              ].map((badge) => (
                <div key={badge.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-yellow-400">{badge.value}</div>
                  <div className="text-xs md:text-sm text-gray-300 mt-1">{badge.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 10, 0] }}
            transition={{ delay: 1.2, y: { duration: 2, repeat: Infinity } }}
            className="absolute bottom-8 flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="text-gray-400 text-xs uppercase tracking-widest">Scroll</span>
            <ChevronDown className="w-5 h-5 text-yellow-400" />
          </motion.div>
        </div>
      </section>

      {/* ============ STATS COUNTER SECTION ============ */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #FACC15 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <div ref={statsRef} className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Building2, end: 10, suffix: '+', label: 'Years of Excellence' },
              { icon: Target, end: 200, suffix: '+', label: 'Projects Delivered' },
              { icon: Users, end: 50, suffix: '+', label: 'Skilled Professionals' },
              { icon: Shield, end: 100, suffix: '%', label: 'Quality Commitment' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col items-center"
              >
                <stat.icon className="w-8 h-8 text-yellow-400 mb-4" />
                <span className="text-4xl md:text-5xl font-extrabold text-white mb-2">
                  <Counter end={stat.end} suffix={stat.suffix} />
                </span>
                <span className="text-blue-200 text-sm md:text-base">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT US SECTION ============ */}
      <section id="about-section" className="py-24 bg-gradient-to-br from-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-800 rounded-full filter blur-3xl translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl -translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2"
            >
              <div className="relative">
                <div className="relative z-20 rounded-2xl overflow-hidden shadow-2xl">
                  <img src={about} alt="About BuildFast" className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-800/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-yellow-400 rounded-xl -z-10"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-800 rounded-xl -z-10"></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <motion.div whileHover={{ y: -5 }} className="bg-white p-4 rounded-xl shadow-lg">
                  <h4 className="text-4xl font-bold text-blue-800">10+</h4>
                  <p className="text-gray-600">Years Experience</p>
                </motion.div>
                <motion.div whileHover={{ y: -5 }} className="bg-white p-4 rounded-xl shadow-lg">
                  <h4 className="text-4xl font-bold text-blue-800">200+</h4>
                  <p className="text-gray-600">Projects Completed</p>
                </motion.div>
              </div>
            </motion.div>
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2 space-y-6"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-1 w-12 bg-yellow-400"></div>
                  <span className="text-blue-800 font-semibold">About Us</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900">Building Dreams Into Reality</h2>
              </div>
              <div className="space-y-4 text-gray-600">
                <p className="text-lg">At BuildFast Structural Solutions, we are dedicated to transforming visions into reality through innovative and sustainable design. Our experienced team is committed to delivering exceptional results that inspire and enhance the human experience.</p>
                <p className="text-lg">With a team of visionary architects and design professionals, we specialize in creating dynamic environments that blend functionality with aesthetic appeal.</p>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-8">
                {[
                  { icon: Target, title: "Vision", text: "Clear project direction" },
                  { icon: Shield, title: "Quality", text: "Premium materials" },
                  { icon: Clock, title: "Timely", text: "On-time delivery" },
                  { icon: Users, title: "Team", text: "Expert professionals" }
                ].map((feature, index) => (
                  <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <div className="p-2 bg-blue-800/10 rounded-lg"><feature.icon className="w-6 h-6 text-blue-800" /></div>
                    <div><h4 className="font-semibold text-gray-900">{feature.title}</h4><p className="text-sm text-gray-600">{feature.text}</p></div>
                  </motion.div>
                ))}
              </div>
              <motion.button onClick={handleClick} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="mt-8 bg-blue-800 text-white font-bold py-4 px-8 rounded-xl hover:bg-blue-700 transition-colors duration-300 flex items-center gap-2 group">
                Learn More About Us
                <ChevronRight className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES SECTION ============ */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <motion.div initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }} className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="flex items-center justify-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-yellow-400"></div>
              <span className="text-blue-800 font-semibold text-sm tracking-wider uppercase">What We Offer</span>
              <div className="h-0.5 w-8 bg-yellow-400"></div>
            </motion.div>
            <motion.h2 initial={{ y: -20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }} className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-gray-500 max-w-2xl mx-auto">
              Comprehensive construction and design solutions tailored to bring your vision to life
            </motion.p>
          </div>
          <Swiper modules={[Navigation, Pagination, Autoplay]} spaceBetween={30} slidesPerView={1} breakpoints={{ 640: { slidesPerView: 1 }, 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }} navigation pagination={{ clickable: true }} autoplay={{ delay: 5000 }} loop={true} className="services-swiper">
            {services.map((service, index) => (
              <SwiperSlide key={index}>
                <motion.div whileHover={{ y: -10 }} transition={{ duration: 0.3 }} className="bg-white rounded-2xl shadow-2xl overflow-hidden group cursor-pointer h-full" onClick={() => handleServiceClick(service.title)}>
                  <div className="relative h-48 overflow-hidden">
                    <img className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" src={service.image} alt={service.title} />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-800/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6 relative">
                    <div className="absolute -top-8 right-6 w-16 h-16 bg-yellow-400 rounded-xl shadow-lg flex items-center justify-center transform -rotate-12 group-hover:rotate-0 transition-transform duration-300">
                      <service.icon className="w-8 h-8 text-blue-800" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-800 mb-3 group-hover:text-yellow-400 transition-colors">{service.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{service.description}</p>
                    <div className="flex items-center text-yellow-400 font-semibold group-hover:text-blue-800 transition-colors">
                      Learn More <ChevronRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </section>

      {/* ============ PROJECTS SECTION ============ */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-800 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <motion.div initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }} className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <motion.span initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="text-blue-800 font-semibold mb-4 flex items-center justify-center gap-2">
              <div className="h-1 w-12 bg-yellow-400"></div> Our Portfolio <div className="h-1 w-12 bg-yellow-400"></div>
            </motion.span>
            <motion.h2 initial={{ y: -20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Recent Projects</motion.h2>
            <motion.p initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="max-w-2xl mx-auto text-gray-600">Explore our latest architectural masterpieces and innovative designs that showcase our commitment to excellence.</motion.p>
          </div>
          <Swiper modules={[Navigation, Pagination, Autoplay]} spaceBetween={30} slidesPerView={1} breakpoints={{ 640: { slidesPerView: 1 }, 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }} navigation pagination={{ clickable: true }} autoplay={{ delay: 5000 }} loop={true} className="projects-swiper">
            {projects.map((project, index) => (
              <SwiperSlide key={index}>
                <motion.div whileHover={{ y: -10 }} transition={{ duration: 0.3 }} className="bg-white rounded-2xl shadow-xl overflow-hidden group cursor-pointer" onClick={handleProjectClick}>
                  <div className="relative h-64 overflow-hidden">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-800/90 via-blue-800/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-6 group-hover:translate-y-0 transition-transform duration-300">
                        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="bg-yellow-400 text-blue-800 font-semibold py-2 px-4 rounded-lg flex items-center gap-2">
                          View Details <ChevronRight size={16} />
                        </motion.button>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-blue-800 mb-2 group-hover:text-yellow-400 transition-colors">{project.title}</h3>
                      <div className="h-0.5 w-12 bg-yellow-400 group-hover:w-full transition-all duration-300"></div>
                    </div>
                    <p className="text-gray-600 line-clamp-3">{project.description}</p>
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <div className="flex justify-between text-sm">
                        <div className="flex items-center gap-2"><span className="w-4 h-4 text-blue-800">📍</span><span className="text-gray-600">Harare, Zimbabwe</span></div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="text-center mt-12">
            <motion.button onClick={handleProjectClick} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="bg-blue-800 text-white font-bold py-4 px-8 rounded-xl hover:bg-blue-700 transition-colors duration-300 flex items-center gap-2 mx-auto group">
              View All Projects <ChevronRight className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </motion.div>
      </section>

      {/* ============ TESTIMONIALS SECTION ============ */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-800 rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-yellow-400"></div>
              <span className="text-blue-800 font-semibold text-sm tracking-wider uppercase">Testimonials</span>
              <div className="h-0.5 w-8 bg-yellow-400"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Don't just take our word for it — hear from the people we've built for</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                whileHover={{ y: -8 }}
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-xl border border-gray-100 relative group hover:border-yellow-400/50 transition-all duration-300"
              >
                <Quote className="w-10 h-10 text-yellow-400/20 absolute top-6 right-6 group-hover:text-yellow-400/40 transition-colors" />
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-6 border-t border-gray-100">
                  <div className="w-12 h-12 rounded-full bg-blue-800 flex items-center justify-center text-white font-bold text-lg">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{t.name}</h4>
                    <p className="text-sm text-gray-500">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING SECTION ============ */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <motion.div initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }} className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="flex items-center justify-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-yellow-400"></div>
              <span className="text-blue-800 font-semibold text-sm tracking-wider uppercase">Pricing Plans</span>
              <div className="h-0.5 w-8 bg-yellow-400"></div>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Products and Prices</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Transparent pricing for architectural house plans. All prices are per square meter.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.size}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className={`relative bg-white p-8 rounded-2xl shadow-xl border-2 transition-all duration-300 ${tier.popular ? 'border-yellow-400 shadow-yellow-400/10 shadow-2xl' : 'border-gray-100 hover:border-yellow-400/50'}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 text-sm font-bold px-6 py-1.5 rounded-full shadow-lg">
                    Most Popular
                  </div>
                )}
                <div className={`text-center mb-6 ${tier.popular ? 'mt-4' : ''}`}>
                  <span className="text-5xl font-extrabold text-blue-800">
                    ${tier.price}
                  </span>
                  <span className="text-gray-400 text-sm">/plan</span>
                </div>
                <h3 className={`text-xl font-bold text-center mb-6 pb-6 border-b ${tier.popular ? 'text-yellow-500' : 'text-gray-800'}`}>
                  {tier.size}
                </h3>
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, fi) => (
                    <li key={fi} className="flex items-start gap-3">
                      <div className="p-1 bg-green-100 rounded-full mt-0.5 flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <motion.button
                  onClick={handleQuotation}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full font-bold py-4 px-4 rounded-xl transition-all duration-300 ${tier.popular ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300 shadow-lg shadow-yellow-400/25' : 'bg-blue-800 text-white hover:bg-blue-700'}`}
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ============ CTA BANNER SECTION ============ */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #FACC15 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            Ready to Build Your <span className="text-yellow-400">Dream Project</span>?
          </h2>
          <p className="text-lg text-blue-200 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let's discuss your vision. Our team of experts is ready to bring your ideas to life with precision, quality, and unmatched dedication.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={handleQuotation}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-yellow-400 text-blue-900 font-bold py-4 px-10 rounded-xl text-lg hover:bg-yellow-300 transition-all duration-300 shadow-2xl shadow-yellow-400/25 flex items-center gap-2"
            >
              Get Your Free Quote
              <ChevronRight size={20} />
            </motion.button>
            <motion.button
              onClick={scrollToContact}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-bold py-4 px-10 rounded-xl text-lg hover:bg-white/20 transition-all duration-300"
            >
              Contact Us
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* ============ CONTACT US SECTION ============ */}
      <section id="contact-section" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-100 to-white">
        <motion.div initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }} className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-yellow-400"></div>
              <span className="text-blue-800 font-semibold text-sm tracking-wider uppercase">Get In Touch</span>
              <div className="h-0.5 w-8 bg-yellow-400"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Contact Us</h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-8">
            <motion.div className="flex-1 bg-white rounded-lg shadow-xl p-8 border border-gray-200" whileHover={{ boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }} transition={{ duration: 0.3 }}>
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                {[
                  { id: 'name', type: 'text', label: 'Your Name' },
                  { id: 'email', type: 'email', label: 'Your Email' },
                  { id: 'phone', type: 'tel', label: 'WhatsApp/Phone Number' },
                ].map((field) => (
                  <div key={field.id} className="relative">
                    <input type={field.type} id={field.id} name={field.id} className="peer w-full px-3 py-3 border-b-2 border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-blue-500 transition-colors" placeholder={field.label} required />
                    <label htmlFor={field.id} className="absolute left-3 -top-5 text-sm text-gray-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-5 peer-focus:text-sm peer-focus:text-blue-500">{field.label}</label>
                  </div>
                ))}
                <div className="relative">
                  <textarea id="message" name="message" rows="4" className="peer w-full px-3 py-3 border-b-2 border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-blue-500 transition-colors resize-none" placeholder="Your Message" required></textarea>
                  <label htmlFor="message" className="absolute left-3 -top-5 text-sm text-gray-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-5 peer-focus:text-sm peer-focus:text-blue-500">Your Message</label>
                </div>
                <motion.button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-3 px-4 rounded-lg transition duration-300 ease-in-out flex items-center justify-center group hover:from-cyan-600 hover:to-blue-600" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Send className="mr-2 transition-transform duration-300 group-hover:translate-x-1" size={20} /> Send Message
                </motion.button>
              </form>
              {formStatus && (
                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 text-green-600 text-center">{formStatus}</motion.p>
              )}
            </motion.div>
            <motion.div className="flex-1 bg-white rounded-lg shadow-xl p-8 border border-gray-200" whileHover={{ boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }} transition={{ duration: 0.3 }}>
              <div className="space-y-6">
                {[
                  { icon: Mail, label: 'Email', value: 'info@buildfast.co.zw', href: 'mailto:info@buildfast.co.zw' },
                  { icon: Phone, label: 'Phone', value: '+263 776 347 735', href: 'tel:+263776347735' },
                  { icon: MapPin, label: 'Address', value: 'Margoliz Plaza Cnr Harare st & Speke Ave, Harare' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center space-x-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                    <div className="p-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full text-white"><item.icon size={24} /></div>
                    <div>
                      <p className="text-sm text-gray-500">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-gray-900 hover:text-blue-500 transition-colors">{item.value}</a>
                      ) : (
                        <p className="text-gray-900">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="mt-8 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                  <h4 className="text-xl font-semibold mb-4 text-gray-900">Office Hours</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center"><span className="w-4 h-4 bg-green-500 rounded-full mr-2"></span> Monday - Friday: 9:00 AM - 5:00 PM</li>
                    <li className="flex items-center"><span className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></span> Saturday: 10:00 AM - 2:00 PM</li>
                    <li className="flex items-center"><span className="w-4 h-4 bg-red-500 rounded-full mr-2"></span> Sunday: Closed</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </main>
  );
};
